/**
 * MatchStartVotePane — first-round vote screen, shown when the host
 * presses Start in a vote-mode lobby. Matches the post-round vote UI
 * but with a session-opening header instead of a round recap.
 */
import { useEffect, useState } from 'react';
import type { LobbyView } from '@tt/lobby';
import { getCharacterStats, type CharacterStats, type GameTile } from '../../lib/api';
import { VoteTiles } from './VoteTiles';

interface Props {
  lobby: LobbyView;
  vote: { gameIds: string[]; endsAt: number; tally: Record<string, number> } | null;
  games: GameTile[];
  myCharId: string;
  isHost: boolean;
  onVote: (gameId: string) => void;
  onSkip: () => void;
  onInspectPlayer?: (charId: string) => void;
}

export function MatchStartVotePane({ lobby, vote, games, myCharId, isHost, onVote, onSkip, onInspectPlayer }: Props) {
  const [statsByChar, setStatsByChar] = useState<Record<string, CharacterStats>>({});
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const s = await getCharacterStats(myCharId);
        if (!live) return;
        setStatsByChar({ [myCharId]: s });
      } catch { /* */ }
    })();
    return () => { live = false; };
  }, [myCharId]);

  const livePlayerCount = lobby.players.filter((p) => !p.isSpectator).length;

  return (
    <section className="match-start-vote">
      <header className="match-start-scroll">
        <span className="match-start-seal" aria-hidden="true">T</span>
        <div className="match-start-kicker">The tavern stirs</div>
        <h1 className="match-start-title">Choose the first match</h1>
        <p className="match-start-sub">{livePlayerCount} player{livePlayerCount === 1 ? '' : 's'} ready · {modeLabel(lobby.settings.mode)} · best of {lobby.settings.roundsPerSet}</p>
      </header>

      {vote ? (
        <VoteTiles
          vote={vote}
          games={games}
          myCharId={myCharId}
          lobby={lobby}
          statsByChar={statsByChar}
          onVote={onVote}
          onInspectPlayer={onInspectPlayer}
        />
      ) : (
        <p className="muted match-start-pending">Drawing options…</p>
      )}

      {isHost && vote && (
        <div className="match-start-skip">
          <button type="button" className="btn ghost" onClick={onSkip}>Skip the vote — start now</button>
        </div>
      )}
    </section>
  );
}

function modeLabel(mode: LobbyView['settings']['mode']): string {
  switch (mode) {
    case 'free': return 'Free play';
    case 'set': return 'Set play';
    case 'vote': return 'Vote mode';
    default: return String(mode);
  }
}
